import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRevalidator, useRouteError } from 'react-router-dom'

type PageLoadingProps = {
  fullScreen?: boolean
  delay?: number
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function PageLoading({ fullScreen = false, delay = 120 }: PageLoadingProps) {
  const { t } = useTranslation('common')
  const [visible, setVisible] = useState(delay === 0)
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion)

  useEffect(() => {
    if (delay === 0) return
    const timer = window.setTimeout(() => setVisible(true), delay)
    return () => window.clearTimeout(timer)
  }, [delay])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReducedMotion(media.matches)
    media.addEventListener?.('change', onChange)
    return () => media.removeEventListener?.('change', onChange)
  }, [])

  if (!visible) return null

  return <div className={`page-loading${fullScreen ? ' page-loading-fullscreen' : ''}`} role="status" aria-live="polite">
    {!reducedMotion && <img className="page-loading-image" src="/loading.gif" alt="" width="72" height="72" />}
    <span>{t('loading', { defaultValue: 'Carregando...' })}</span>
  </div>
}

export function RouteErrorPage() {
  const error = useRouteError()
  const revalidator = useRevalidator()
  const { t } = useTranslation(['common', 'errors'])
  const message = error instanceof Error && error.message
    ? error.message
    : t('errors:generic', { defaultValue: 'Algo deu errado. Tente novamente.' })

  return <main className="page-error" role="alert" aria-live="polite">
    <h1>{t('common:errorTitle', { defaultValue: 'Não foi possível carregar esta página' })}</h1>
    <p>{message}</p>
    <button className="button button-outline" type="button" onClick={() => revalidator.revalidate()} disabled={revalidator.state !== 'idle'}>
      {t('common:retry', { defaultValue: 'Tentar novamente' })}
    </button>
  </main>
}
